import { useFormikContext } from 'formik'
import React, { useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { useSessionEntity } from '@context/EntityContext'

import { Body1, Heading6 } from '@ui/Typography'
import { Checkbox } from '@ui/components/Checkbox'
import { DocumentFileUpload } from '@ui/components/FileUpload/DocumentFileUpload'
import { FileData } from '@ui/components/FileUpload/types'
import { Radio } from '@ui/components/Radio'

import {
  useCategorySizesReadContext,
  useCategorySizesWriteContext,
} from '@utils/CategorySizesContext'
import { isTickmillSCType } from '@utils/companyName.utils'

import { AccountDetailedDto } from '@model/AccountDetailedDto'
import { CountryPreferenceStrippedDto } from '@model/CountryDto'
import { DocumentCategoryType } from '@model/DocumentCategories'
import { NameDto } from '@model/NameDto'

import { FieldGroup } from '@global/form/FieldGroup'
import { createFormCheckBoxField } from '@global/formField/FormCheckBoxField'
import { createFormField } from '@global/formField/FormField'
import { createFormRadioField } from '@global/formField/RadioGroupField'

import { useDropdownState } from '@hooks/useDropdownState'

import { Box } from '@shared/components/Box'
import { FieldWrapper } from '@shared/components/FieldWrapper'
import { PhoneNumberInput } from '@shared/components/PhoneNumberInput/PhoneNumberInput'

import { YearlyKycUpdateDetailsFormValues } from './YearlyKycUpdateDetailsForm'

const FormField = createFormField<YearlyKycUpdateDetailsFormValues>()
const FormRadioGroupField = createFormRadioField()
const FormCheckBoxField = createFormCheckBoxField<YearlyKycUpdateDetailsFormValues>()

interface YearlyKycPersonalInfoFormProps {
  account: AccountDetailedDto | undefined
  proofOfAddressNeeded: () => boolean
  phoneNumberError?: string
  tinUnavailableReasons: NameDto[]
  productRegistrationRequirement?: boolean
}

export const YearlyKycPersonalInfoForm: React.FC<YearlyKycPersonalInfoFormProps> = (props) => {
  const entity = useSessionEntity()
  const { account, proofOfAddressNeeded, phoneNumberError, tinUnavailableReasons } = props
  const { values, setValues, setFieldValue } = useFormikContext<YearlyKycUpdateDetailsFormValues>()
  const { taxIdUnavailableReasonId } = values
  const { t } = useTranslation()

  const initialCountry = useMemo(
    () => values.countries.find((c) => c.phoneCode === values.countryCode),
    [values.countries, values.countryCode]
  )
  const { isOpen, setIsOpen, closeDropdown, dropdownRef } = useDropdownState()
  const { categorySizes } = useCategorySizesReadContext()
  const { refreshCategorySizes } = useCategorySizesWriteContext()

  useEffect(() => {
    refreshCategorySizes()
  }, [])

  const handleOnChangeOption = (country: CountryPreferenceStrippedDto) => {
    setFieldValue('countryCode', country.phoneCode)
    closeDropdown()
  }

  const handleChangePhoneNumber = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFieldValue('phoneNumber', e.target.value)
  }

  const handleDocumentUploads = async (fileData: FileData[]) => {
    setValues({
      ...values,
      documents: fileData,
    })
  }

  const handleDocumentRemove = async (removedDocument: FileData) => {
    const nextDocuments = values.documents.filter((document) => document.id !== removedDocument.id)
    await setFieldValue('documents', nextDocuments, true)
  }

  const isTaxIdCheckboxVisible = useMemo(
    () => !values.taxIdentificationNumber,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  )

  useEffect(() => {
    if (values.taxIdentificationNumber) {
      setFieldValue('taxIdAvailable', true)
      setFieldValue('taxIdUnavailableReasonId', null)
    }
    if (!values.streetHouseNo) {
      setFieldValue('streetHouseNoNotAvailable', true)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values.taxIdentificationNumber, values.streetHouseNo])

  const handleTaxIdAvailableChange = () => {
    setFieldValue('taxIdAvailable', !values.taxIdAvailable)
    setFieldValue('taxIdentificationNumber', '')
  }

  const onChangeStreetAvailability = (checked: boolean) => {
    setFieldValue('streetHouseNoNotAvailable', checked)
    if (checked) {
      setFieldValue('streetHouseNo', '')
    }
  }

  return (
    <>
      <Box
        flex
        direction='column'
        gap='x16'
        p='x24'
        bg='surface-white-smoke-0'
        shadow='e3'
        radius='x16'
        fullWidth
      >
        <Heading6 medium>{t('Profile.Personal Info')}</Heading6>
        <FieldWrapper>
          <PhoneNumberInput
            name='phoneNumber'
            handleChangePhoneNumber={handleChangePhoneNumber}
            values={values.phoneNumber}
            countryCode={values.countryCode}
            countries={values.countries}
            initialCountry={initialCountry}
            isCountryDropdownOpen={isOpen}
            setIsCountryDropdownOpen={setIsOpen}
            handleOnChangeOption={handleOnChangeOption}
            phoneNumberError={phoneNumberError}
            dropdownRef={dropdownRef}
          />
          <FormField name='emailAddress' label={t('Profile.Email Address')} disabled required />
          <FormField
            name='countryName'
            label={t('Profile.Country of Residence')}
            disabled
            required
          />
          <FormField name='street' label={t('Profile.Address line')} required />
          <FormField
            name='streetHouseNo'
            label={t('Sign up.Street/House. Number')}
            disabled={values.streetHouseNoNotAvailable}
            required
          />
          <FormCheckBoxField name='streetHouseNoNotAvailable' onChange={onChangeStreetAvailability}>
            {t('Sign up.Not Available')}
          </FormCheckBoxField>
          <FormField name='city' label={t('Profile.City')} required />

          <FieldGroup>
            <FormField name='state' label={t('Profile.State / Region')} required />
            <FormField name='zipCode' label={t('Profile.Postal Code')} required />
          </FieldGroup>
          {!isTickmillSCType(entity) && (
            <>
              <FormField
                disabled={!values.taxIdAvailable}
                name='taxIdentificationNumber'
                label={t('Profile.Tax Identification Number / Goods & Services Tax Number')}
              />
              {isTaxIdCheckboxVisible && (
                <Checkbox checked={!values.taxIdAvailable} onChange={handleTaxIdAvailableChange}>
                  {t('Profile.Not Available')}
                </Checkbox>
              )}
              {!values.taxIdAvailable && (
                <Box flex direction='column' gap='x16' my='x16'>
                  <Body1 medium>{`${t('Profile.Please select a reason')} *`}</Body1>

                  <FormRadioGroupField
                    name='taxIdUnavailableReasonId'
                    value={taxIdUnavailableReasonId}
                    required
                  >
                    {tinUnavailableReasons.map(({ id, name }) => (
                      <Radio
                        key={name}
                        name='taxIdUnavailableReasonId'
                        value={id}
                        checked={id === Number(taxIdUnavailableReasonId)}
                      >
                        {name}
                      </Radio>
                    ))}
                  </FormRadioGroupField>
                </Box>
              )}
            </>
          )}
        </FieldWrapper>
      </Box>

      {account?.isKYCUpdateNeeded && proofOfAddressNeeded() && (
        <div>
          <Body1 medium>{t('Profile.Proof Of Address')}</Body1>
          <p className='content'>
            {t('The image must be high quality, unobstructed and show the full document page.')}
          </p>
          <DocumentFileUpload
            categorySizes={categorySizes}
            documentCategory={DocumentCategoryType.Address}
            onUpload={handleDocumentUploads}
            onRemove={handleDocumentRemove}
            multiple
          />
        </div>
      )}
    </>
  )
}
